import { constants, DISCOUNT_TYPES, LOYALTY_DISCOUNT_TYPES } from "../constant/constants";
import ApiService from "../utils/ApiService";
import { getBasePriceFromIncludedPrice, getParsedNumber, roundNumber } from "../utils/Common";
import Helper from "../utils/Helper";
import { getPaymentCommonInfo } from "./order-common-service";

export const defaultSummaryData = {
  subtotal: 0,
  sd: 0,
  serviceChargePercent: 0,
  serviceCharge: 0,
  vatPercent: 0,
  vat: 0,
  grossTotal: 0,
};

export const defaultRoundedValues = {
  roundedPayable: 0,
  decimal: 0,
  sign: "",
};

export const shoudShowDiscountAmount = (discountType, discount, isCouponEligible) => {
  if (discountType === constants.DISCOUNT_TYPE.PERCENTAGE && getParsedNumber(discount) > 0) {
    return true;
  }

  if (discountType === "Coupon" && isCouponEligible) {
    return true;
  }

  return false;
};

export const getDefaultDiscountInfo = (order, defaultDiscountTypes) => {
  console.log("--getDefaultDiscountInfo--getDefaultDiscountInfo", { order, defaultDiscountTypes });
  let disType = defaultDiscountTypes[0].label;
  const disObj = {
    discount: "",
    discountType: disType,
    calculativeDisType: disType,
    isCouponEligible: false,
    couponCode: "",
  };

  if (!order || Object.keys(order).length < 5) {
    return disObj;
  }

  if (!order.staffDiscount && !order.loyaltyDiscount && !order.couponDiscount) {
    return disObj;
  }

  if (order.staffDiscount) {
    disType = order.staffDiscount.isDiscountInPercent ? DISCOUNT_TYPES[1].label : DISCOUNT_TYPES[0].label;
    return {
      ...disObj,
      discount: order.staffDiscount.discount,
      discountType: disType,
      calculativeDisType: disType,
    };
  }

  if (order.loyaltyDiscount) {
    disType =
      order.loyaltyDiscount.discountType === "REDEEM"
        ? LOYALTY_DISCOUNT_TYPES[0].label
        : LOYALTY_DISCOUNT_TYPES[1].label;

    return {
      ...disObj,
      discount:
        order.loyaltyDiscount.discountType === "REDEEM"
          ? order.loyaltyDiscount.redeemedPoints
          : order.loyaltyDiscount.discountPercent,
      discountType: disType,
      calculativeDisType: disType,
    };
  }

  if (order.couponDiscount) {
    disType = order.couponDiscount.isDiscountInPercent ? DISCOUNT_TYPES[1].label : DISCOUNT_TYPES[0].label;
    return {
      ...disObj,
      isCouponEligible: true,
      discount: order.couponDiscount.appliedDiscount,
      discountType: "Coupon",
      calculativeDisType: disType,
    };
  }

  return disObj;
};

export const applyDiscount = (totalAmount, discountApplicablePrice, discount, discountType) => {
  let totalPayable = getParsedNumber(totalAmount);
  let disc = getParsedNumber(discount);

  if (["Percentage", "Spend Based"].includes(discountType)) {
    disc = roundNumber((discountApplicablePrice * disc) / 100);
  }

  return {
    priceAfterDiscount: roundNumber(totalPayable - disc),
    discountAmount: disc,
  };
};

export const getPriceSummaryVatIncluded = (basePrice, discountApplicablePrice, discount, discountType) => {
  const discountObj = applyDiscount(basePrice, discountApplicablePrice, discount, discountType);

  return {
    discountAmount: discountObj.discountAmount,
    priceAfterDiscount: discountObj.priceAfterDiscount,
    serviceCharge: 0,
    sd: 0,
    vat: 0,
    grossTotal: discountObj.priceAfterDiscount,
  };
};

export const calculateReturnAmount = (paymentMethods, totalPayable, roundedValues, isRoundingApplicable) => {
  let total = paymentMethods.reduce((sum, item) => sum + getParsedNumber(item.amount), 0);

  total = getParsedNumber(total);

  if (total === 0) {
    return 0;
  }

  if (!isRoundingApplicable) {
    return roundNumber(totalPayable - total);
  }

  return roundNumber(roundedValues.roundedPayable - total);
};

export const getPriceSummaryVatExcluded = (order, discount, isDiscountInPercent) => {
  let {
    vatApplicableDiscountApplicableSubtotal,
    vatApplicableDiscountNotApplicableSubtotal,
    vatNotApplicableDiscountApplicableSubtotal,
    vatNotApplicableDiscountNotApplicableSubtotal,
  } = order.subtotalObj;

  let {
    vatApplicableDiscountApplicableSd,
    vatApplicableDiscountNotApplicableSd,
    vatNotApplicableDiscountApplicableSd,
    vatNotApplicableDiscountNotApplicableSd,
  } = order.sdObj;

  const discountAmount = getDiscountAmount(
    discount,
    isDiscountInPercent,
    vatApplicableDiscountApplicableSubtotal + vatNotApplicableDiscountApplicableSubtotal,
    order.foodPriceBeforeDiscount,
  );

  let discountPercent = getParsedNumber(discount);

  if (isDiscountInPercent) {
    vatApplicableDiscountApplicableSubtotal = getDiscountedPrice(
      vatApplicableDiscountApplicableSubtotal,
      discountPercent,
      true,
    );
    vatNotApplicableDiscountApplicableSubtotal = getDiscountedPrice(
      vatNotApplicableDiscountApplicableSubtotal,
      discountPercent,
      true,
    );

    vatApplicableDiscountApplicableSd = getDiscountedPrice(vatApplicableDiscountApplicableSd, discountPercent, true);
    vatNotApplicableDiscountApplicableSd = getDiscountedPrice(
      vatNotApplicableDiscountApplicableSd,
      discountPercent,
      true,
    );
  } else {
    // discountPercent = roundNumber((discountAmount / order.foodPriceBeforeDiscount) * 100); // commented while testing coupon for fixed amount discount 0.01 diff comes
    discountPercent = (discountAmount / order.foodPriceBeforeDiscount) * 100;

    vatApplicableDiscountApplicableSubtotal = getDiscountedPrice(
      vatApplicableDiscountApplicableSubtotal,
      discountPercent,
      true,
    );
    vatApplicableDiscountNotApplicableSubtotal = getDiscountedPrice(
      vatApplicableDiscountNotApplicableSubtotal,
      discountPercent,
      true,
    );
    vatNotApplicableDiscountApplicableSubtotal = getDiscountedPrice(
      vatNotApplicableDiscountApplicableSubtotal,
      discountPercent,
      true,
    );
    vatNotApplicableDiscountNotApplicableSubtotal = getDiscountedPrice(
      vatNotApplicableDiscountNotApplicableSubtotal,
      discountPercent,
      true,
    );

    vatApplicableDiscountApplicableSd = getDiscountedPrice(vatApplicableDiscountApplicableSd, discountPercent, true);
    vatApplicableDiscountNotApplicableSd = getDiscountedPrice(
      vatApplicableDiscountNotApplicableSd,
      discountPercent,
      true,
    );
    vatNotApplicableDiscountApplicableSd = getDiscountedPrice(
      vatNotApplicableDiscountApplicableSd,
      discountPercent,
      true,
    );
    vatNotApplicableDiscountNotApplicableSd = getDiscountedPrice(
      vatNotApplicableDiscountNotApplicableSd,
      discountPercent,
      true,
    );
  }

  let vatApplicableTotalValue =
    vatApplicableDiscountApplicableSubtotal +
    vatApplicableDiscountNotApplicableSubtotal +
    vatApplicableDiscountApplicableSd +
    vatApplicableDiscountNotApplicableSd;

  let vatNotApplicableFoodPrice =
    vatNotApplicableDiscountApplicableSubtotal + vatNotApplicableDiscountNotApplicableSubtotal;

  let foodPrice = roundNumber(order.foodPriceBeforeDiscount - discountAmount);

  const sd =
    vatApplicableDiscountApplicableSd +
    vatApplicableDiscountNotApplicableSd +
    vatNotApplicableDiscountApplicableSd +
    vatNotApplicableDiscountNotApplicableSd;

  console.log("==================sd=====================", {
    vatApplicableDiscountApplicableSd,
    vatApplicableDiscountNotApplicableSd,
    vatNotApplicableDiscountApplicableSd,
    vatNotApplicableDiscountNotApplicableSd,
  });

  if (order.isSupplementaryDutyIncluded) {
    foodPrice -= sd;

    vatApplicableTotalValue =
      vatApplicableTotalValue - (vatApplicableDiscountApplicableSd + vatApplicableDiscountNotApplicableSd);

    vatNotApplicableFoodPrice =
      vatNotApplicableFoodPrice - (vatNotApplicableDiscountApplicableSd + vatNotApplicableDiscountNotApplicableSd);
  }

  let serviceCharge = roundNumber((order.serviceChargePercent / 100) * foodPrice);

  if (order.isServiceChargeIncluded) {
    let foodPriceWithoutScAndSdAndVat = getBasePriceFromIncludedPrice(foodPrice, order.serviceChargePercent);

    serviceCharge = roundNumber(foodPrice - roundNumber(foodPriceWithoutScAndSdAndVat));
    foodPrice -= serviceCharge;
    foodPrice = roundNumber(foodPrice);

    // should calculate base price first, because its serviceCharge included
    let vatNotApplicableFoodPriceWithoutSc = getBasePriceFromIncludedPrice(
      vatNotApplicableFoodPrice,
      order.serviceChargePercent,
    );

    let serviceChargeOfVatNotApplicableFoodPrice = vatNotApplicableFoodPrice - vatNotApplicableFoodPriceWithoutSc;

    vatApplicableTotalValue += serviceChargeOfVatNotApplicableFoodPrice; // if SC included then vatApplicableTotalValue is already included service charge of vat applicable subtotal, so additionally adding the service charge of vat not applicable part (before apply vat).
  } else {
    vatApplicableTotalValue += serviceCharge; // if sc is excluded serviceCharge should be added with vatApplicableTotalValue (before apply vat).
  }

  const vat = roundNumber((order.vatPercent / 100) * vatApplicableTotalValue);

  const totalPrice = foodPrice + sd + serviceCharge + vat;

  const discountedSubtotal =
    vatApplicableDiscountApplicableSubtotal +
    vatApplicableDiscountNotApplicableSubtotal +
    vatNotApplicableDiscountApplicableSubtotal +
    vatNotApplicableDiscountNotApplicableSubtotal;

  return {
    discountAmount,
    priceAfterDiscount: discountedSubtotal,
    serviceCharge,
    sd: roundNumber(sd),
    vat,
    grossTotal: roundNumber(totalPrice),
  };
};

export const getPriceSummary = (order, discount, discountType, paymentMethods, roundedValues, isRoundingApplicable) => {
  console.log("******getPriceSummary*****called*******", {
    discount,
    discountType,
  });

  const basePrice = order.isVatIncluded ? order.totalPriceBeforeDiscount : order.foodPriceBeforeDiscount;

  let calcValues = null;

  if (order.isVatIncluded) {
    calcValues = getPriceSummaryVatIncluded(basePrice, order.discountApplicablePrice, discount, discountType);
  }

  if (!order.isVatIncluded) {
    const isDiscountInPercent =
      discountType === constants.DISCOUNT_TYPE.PERCENTAGE || discountType === "Spend Based" ? true : false;
    calcValues = getPriceSummaryVatExcluded(order, discount, isDiscountInPercent);
  }

  const returnedAmount = calculateReturnAmount(
    paymentMethods,
    calcValues.grossTotal,
    roundedValues,
    isRoundingApplicable,
  );

  return {
    calcValues,
    returnedAmount,
  };
};

export const getSummaryData = (order) => {
  return {
    subtotal: order.isVatIncluded ? order.totalPriceBeforeDiscount : order.foodPriceBeforeDiscount,
    sd: order.sd,
    serviceCharge: order.serviceCharge,
    serviceChargePercent: order.serviceChargePercent,
    vat: order.vat,
    vatPercent: order.vatPercent,
    grossTotal: order.totalPriceBeforeRounding,
  };
};

export const getFormatedFood = (foods) => {
  return foods.map((food) => {
    return {
      ...food,
      uuid: crypto.randomUUID(), // split bill purpose
      isSelected: false, //split bill purpose
      hasBorder: food.additionGroups.length > 0 || food.addOns.length > 0,
      additionGroups: Helper.transformAdditionGroups(food.additionGroups).map((item) => ({
        ...item,
        isSelected: true,
      })),
    };
  });
};

export const toggleFoodSelection = (uuid, foods) => {
  return foods.map((item) => ({
    ...item,
    isSelected: item.uuid === uuid ? !item.isSelected : item.isSelected,
  }));
};

export const getCalculatedPriceSummary = (foods, isVatIncluded, serviceChargePercent, vatPercent) => {
  let totalFoodPrice = 0;
  let discountApplicablePrice = 0;

  foods.forEach((item) => {
    if (!item.isSelected) {
      return;
    }

    totalFoodPrice += item.option.price;
    const addOnsPrice = item.addOns.reduce((sum, addOn) => sum + addOn.price, 0);
    totalFoodPrice = roundNumber(totalFoodPrice + addOnsPrice);
    const groupPrice = item.additionGroups.reduce((sum, gr) => sum + getParsedNumber(gr.totalPriceOfSelectedItems), 0);
    totalFoodPrice += groupPrice;
    // Discount Applicable price
    if (item.isDiscountApplicable) {
      discountApplicablePrice += item.option.price;
      discountApplicablePrice = roundNumber(discountApplicablePrice + addOnsPrice);
      discountApplicablePrice += groupPrice;
    }
  });

  const serviceCharge = roundNumber((totalFoodPrice * serviceChargePercent) / 100);
  const netTotal = roundNumber(totalFoodPrice + serviceCharge);
  const vat = roundNumber((netTotal * vatPercent) / 100);

  return {
    subtotal: totalFoodPrice,
    discountApplicablePrice,
    serviceCharge,
    vat,
    foodPriceBeforeDiscount: totalFoodPrice,
    totalPriceBeforeDiscount: totalFoodPrice,
    grossTotal: isVatIncluded ? totalFoodPrice : roundNumber(netTotal + vat),
  };
};

export const validateSplitBillData = (paymentInfo, foods) => {
  const selectedFoods = foods.filter((item) => item.isSelected);

  if (selectedFoods.length === 0) {
    return "Please, select at least one food";
  }

  if (foods.length === selectedFoods.length) {
    return "Selecting all foods is not allowed";
  }

  if (paymentInfo.totalPayable < 0) {
    return "Invalid discount.";
  }

  const { paymentReceiveTypes, totalReturnAmount } = getPaymentCommonInfo(
    paymentInfo.paymentMethods,
    paymentInfo.isSplitted,
    paymentInfo.defaultRef,
    paymentInfo.posBankId,
    paymentInfo.totalPayable,
    paymentInfo.discountType,
    paymentInfo.discount,
  );

  if (paymentReceiveTypes.length === 0 && paymentInfo.totalPayable !== 0) {
    return "No payment method provided with value.";
  }

  if (totalReturnAmount < 0) {
    return "Payment amount cannot be less than total payable amount";
  }

  const posInfo = paymentReceiveTypes.find((item) => item.paymentReceiveType === "CARD");

  if (posInfo !== undefined && posInfo.posBankId.length === 0) {
    return "Please select bank for POS payment.";
  }

  return "";
};

export const sendSplitBillReq = async (order, paymentInfo, foods, grossTotal, token) => {
  const result = {
    success: true,
    message: "Failed to split. Please try again",
    record: {},
  };

  try {
    const {
      paymentReceiveTypes,
      totalPaidAmount,
      totalReturnAmount,
      amount,
      actualDiscount,
      isDiscountInPercent,
      loyaltyDiscount,
    } = getPaymentCommonInfo(
      paymentInfo.paymentMethods,
      paymentInfo.isSplitted,
      paymentInfo.defaultRef,
      paymentInfo.posBankId,
      paymentInfo.totalPayable,
      paymentInfo.discountType,
      paymentInfo.discount,
    );

    const reqBody = {
      orderId: order.orderId,
      paymentPurpose: "ORDER",
      totalPrice: grossTotal,
      selectedFoodIndexes: foods.filter((item) => item.isSelected).map((item) => item.index),
      paymentReceiveTypes,
      totalPaidAmount,
      totalReturnAmount,
      amount,
      discount: paymentInfo.couponCode ? "" : actualDiscount,
      isDiscountInPercent,
      loyaltyDiscount,
      discountNote: "",
      couponCode: paymentInfo.couponCode,
    };

    const res = await ApiService.SPLIT_BILL(reqBody, token);

    if (res.code === 200) {
      result.message = "";
      result.record = res.payload.record;
      return result;
    }

    result.success = false;
    result.message = res.message;

    return result;
  } catch (error) {
    result.success = false;
    return result;
  }
};

export const shouldDisplayDiscountableAmount = (order, discountType, coupons, couponCode) => {
  if (discountType === DISCOUNT_TYPES[0].label || discountType === LOYALTY_DISCOUNT_TYPES[0].label) {
    return false;
  }

  const discountApplicable =
    order.subtotalObj.vatApplicableDiscountApplicableSubtotal +
    order.subtotalObj.vatNotApplicableDiscountApplicableSubtotal;
  const discountNotApplicable =
    order.subtotalObj.vatApplicableDiscountApplicableSubtotal +
    order.subtotalObj.vatNotApplicableDiscountApplicableSubtotal;

  if (discountType === "Coupon") {
    const codeInfo = (coupons || []).find(item => item.value === couponCode);
    const isCouponConditionApplicable = (codeInfo && codeInfo.isDiscountInPercent) ? true : false;
    return discountApplicable >= 0 && discountNotApplicable >= 0 && isCouponConditionApplicable;
  }

  if (discountApplicable > 0 && discountNotApplicable > 0) {
    return true;
  }

  return false;
};

export const getDiscountAmount = (discount, isDiscountInPercent, percentDiscountAppliedOn, maxDiscount = 0) => {
  let discountAmount = getParsedNumber(discount);

  if (isDiscountInPercent === "YES" || isDiscountInPercent === true) {
    discountAmount = roundNumber((discountAmount / 100) * percentDiscountAppliedOn);
  }

  return discountAmount;
};
export const getDiscountedPrice = (price, discount, isDiscountInPercent) => {
  const parsedPrice = getParsedNumber(price);
  let parsedDiscount = getParsedNumber(discount);

  if (!isDiscountInPercent) {
    return parsedPrice - parsedDiscount;
  }

  parsedDiscount = (parsedPrice * parsedDiscount) / 100;

  const discountedPrice = parsedPrice - parsedDiscount;
  return discountedPrice;
};
